import { jsx as _jsx } from "react/jsx-runtime";
import { useTheme } from '@mui/material';
import MuiTooltip, { tooltipClasses as muiTooltipClasses } from '@mui/material/Tooltip';
import * as React from 'react';
import { useClickAway, useMounted } from '../hooks';
import { Markdown } from '../markdown';
import { withVersion } from '../utils';
const preventEmailLinks = (text) => text.replace(/<([^>]+@[^>]+\.[^>]+)>/g, '\\<$1>');
const replaceNewlineWithBr = (text) => text.replace(/\n/g, '<br>');
function getTooltipTitle(tooltip) {
    if (typeof tooltip === 'string') {
        return (_jsx(Markdown, { children: preventEmailLinks(replaceNewlineWithBr(tooltip)) }));
    }
    return tooltip;
}
function useDisabled(disabled, popperRef) {
    const [internalDisabled, setInternalDisabled] = React.useState(false);
    const mounted = useMounted();
    const timeoutRef = React.useRef(null);
    const onMouseEnter = () => {
        clearTimeout(timeoutRef.current);
        setInternalDisabled(false);
    };
    /**
     * This is a workaround disabling the tooltip when the mouse leaves the tooltip.
     */
    const onMouseLeave = () => {
        const popper = popperRef.current;
        let targetIsPopper = false;
        const onMouseMove = (e) => {
            if (e.target === popper || popper?.contains(e.target)) {
                targetIsPopper = true;
            }
        };
        popper?.addEventListener('mousemove', onMouseMove);
        timeoutRef.current = setTimeout(() => {
            popper?.removeEventListener('mousemove', onMouseMove);
            if (!targetIsPopper && mounted.current) {
                setInternalDisabled(true);
            }
        }, 150);
    };
    const onClick = () => {
        setInternalDisabled(true);
    };
    React.useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, []);
    return {
        disabled: internalDisabled || disabled,
        tooltipProps: {
            onMouseEnter,
            onMouseLeave,
            onClick,
            onFocus: onMouseEnter,
            onBlur: onMouseLeave
        }
    };
}
export const TooltipImpl = React.forwardRef((props, ref) => {
    const { palette, shadows } = useTheme();
    const [open, setOpen] = React.useState(props.open ?? false);
    const popperRef = React.useRef(null);
    const { onVisibilityChange, mode, disabled: _disabled, bypass = false, slotProps, ...muiProps } = props;
    const { tooltipProps, disabled } = useDisabled(_disabled ?? false, popperRef);
    const onClose = () => {
        setOpen(false);
        onVisibilityChange?.(false);
    };
    const onOpen = () => {
        setOpen(true);
        onVisibilityChange?.(true);
    };
    const titleProps = useClickAway(open, onClose);
    const effectiveDisabled = disabled || bypass;
    const title = effectiveDisabled ||
        props.title === null ||
        props.title === undefined ? null : (_jsx("div", { ...titleProps, children: getTooltipTitle(props.title) }));
    return (_jsx(MuiTooltip, { open: open, ...muiProps, ...tooltipProps, ref: ref, placement: props.placement, onOpen: onOpen, onClose: onClose, "data-role": "mui-tooltip", slotProps: {
            transition: effectiveDisabled
                ? {
                    timeout: 0
                }
                : undefined,
            popper: {
                ref: popperRef,
                sx: mode === 'light'
                    ? // @see https://mui.com/material-ui/react-tooltip/#customization
                        {
                            [`& .${muiTooltipClasses.tooltip}`]: {
                                backgroundColor: palette.background.paper,
                                color: palette.text.primary,
                                boxShadow: shadows[1]
                            }
                        }
                    : {},
                modifiers: [
                    // @see https://mui.com/material-ui/react-tooltip/#distance-from-anchor
                    {
                        name: 'offset',
                        options: {
                            offset: [0, -9]
                        }
                    }
                ]
            },
            ...slotProps
        }, title: title }));
});
export const Tooltip = withVersion(TooltipImpl, '1.0.0');
Tooltip.displayName = 'Tooltip';
export const tooltipStyle = {
    MuiTooltip: {
        styleOverrides: {
            popper: {
                pointerEvents: 'none',
                '.MuiTooltip-tooltip': {
                    pointerEvents: 'auto'
                }
            }
        }
    }
};
